import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { Controller } from "react-hook-form";

import { CurrencySelect, Datepicker, FormCard, Label } from "../../common";
import { InvoiceDetailsDataModal } from "../../modals";
import { useUpdateInvoiceForm } from "../../../hooks/useUpdateInvoiceForm";
import { selectInvoice } from "../../../store/reducers/invoice";
import { selectCurrentUser } from "../../../store/reducers/user";
import {
  checkIssuerDetails,
  checkPayerDetails,
} from "../../../utils/prepareData/invoiceFilledStatus";
import { DEFAULT_FIAT_CURRENCY } from "../../../constants";

export const InvoiceHeaderForm: FC = () => {
  const [side, setSide] = useState<"from" | "to" | undefined>();
  const invoice = useSelector(selectInvoice);
  const { control } = useUpdateInvoiceForm(invoice);

  const {
    allowed_incoming_currencies,
    allowed_outgoing_currencies,
    outgoing_currencies_for_incoming_currency,
  } = useSelector(selectCurrentUser);

  if (!invoice) {
    return <></>;
  }

  const { issuer_data, payer_data, currency } = invoice;

  const selectedIncomingCurrency = currency || DEFAULT_FIAT_CURRENCY;

  const allowedOutgoingCurrencies = outgoing_currencies_for_incoming_currency
    ? outgoing_currencies_for_incoming_currency[selectedIncomingCurrency]
    : [];

  const { message: issuerMessage } = checkIssuerDetails(invoice);
  const { message: payerMessage } = checkPayerDetails(invoice);

  return (
    <div>
      <form className="flex flex-col gap-4">
        <FormCard
          label="From"
          value={issuer_data?.name}
          error={issuerMessage}
          onClick={() => setSide("from")}
        />

        <FormCard
          label="To client"
          value={payer_data?.name}
          error={payerMessage}
          onClick={() => setSide("to")}
        />

        <Label label="Issue date" labelClassName="uppercase">
          <Controller
            control={control}
            name="issued_at"
            render={({ field }) => <Datepicker {...field} />}
          />
        </Label>

        <Label label="Due date to pay" labelClassName="uppercase">
          <Controller
            control={control}
            name="due_at"
            render={({ field }) => <Datepicker {...field} />}
          />
        </Label>

        <Label label="Invoice currency" labelClassName="uppercase">
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <CurrencySelect
                {...field}
                allowedCurrencies={allowed_incoming_currencies}
              />
            )}
          />
        </Label>

        <Label
          label="Currency you'll get"
          className="justify-between"
          labelClassName="mb-4 uppercase !w-fit"
          inline
        >
          <Controller
            name="outgoing_currency"
            control={control}
            render={({ field }) => (
              <CurrencySelect
                {...field}
                allowedCurrencies={allowed_outgoing_currencies.filter((c) =>
                  allowedOutgoingCurrencies.includes(c)
                )}
                otherComingSoon
              />
            )}
          />
        </Label>
      </form>

      {side && (
        <InvoiceDetailsDataModal
          side={side}
          invoice={invoice}
          isOpen={!!side}
          onClose={() => setSide(undefined)}
        />
      )}
    </div>
  );
};
